/**
 * 用户行为跟踪Store (Pinia)
 * 管理用户行为数据收集和分析
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { leadStrategyApi } from '@/api/lead-strategy';

export const useLeadStrategyStore = defineStore('leadStrategy', () => {
  // ============= 状态定义 =============
  
  const sessionId = ref(generateSessionId());
  const behaviorQueue = ref<any[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isTrackingEnabled = ref(true);
  
  // 用户行为统计
  const sessionStats = ref({
    pageViews: 0,
    interactions: 0,
    timeSpent: 0,
    conversions: 0
  });

  // ============= 计算属性 =============
  
  const queueSize = computed(() => behaviorQueue.value.length);
  
  const hasUnsentData = computed(() => queueSize.value > 0);
  
  const sessionDuration = computed(() => {
    if (behaviorQueue.value.length === 0) return 0;
    
    const firstEvent = behaviorQueue.value[0];
    const lastEvent = behaviorQueue.value[behaviorQueue.value.length - 1];
    
    if (!firstEvent || !lastEvent) return 0;
    
    return new Date(lastEvent.timestamp).getTime() - new Date(firstEvent.timestamp).getTime();
  });

  // ============= 工具函数 =============
  
  function generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  // ============= Actions =============
  
  /**
   * 跟踪用户行为
   */
  const trackBehavior = async (eventType: string, eventData: Record<string, any> = {}) => {
    if (!isTrackingEnabled.value) return;
    
    try {
      const behaviorData = {
        sessionId: sessionId.value,
        eventType: eventType,
        eventData: eventData,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer
      };

      // 添加到队列
      behaviorQueue.value.push(behaviorData);
      
      // 更新统计
      updateSessionStats(eventType);

      // 发送到服务器
      await leadStrategyApi.trackUserBehavior(behaviorData);
      
      // 发送成功后从队列中移除
      const index = behaviorQueue.value.findIndex(item => 
        item.timestamp === behaviorData.timestamp
      );
      if (index > -1) {
        behaviorQueue.value.splice(index, 1);
      }
      
    } catch (err: any) {
      error.value = err.message || '行为跟踪失败';
      console.error('行为跟踪失败:', err);
    }
  };
  
  /**
   * 批量发送行为数据
   */
  const flushBehaviorQueue = async () => {
    if (behaviorQueue.value.length === 0) return;
    
    try {
      isLoading.value = true;
      
      const dataToSend = [...behaviorQueue.value];
      await leadStrategyApi.batchTrackBehavior(dataToSend);
      
      // 发送成功后清空队列
      behaviorQueue.value = [];
      
    } catch (err: any) {
      error.value = err.message || '批量发送失败';
      console.error('批量发送行为数据失败:', err);
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * 跟踪页面访问
   */
  const trackPageView = (pageName: string, additionalData?: Record<string, any>) => {
    trackBehavior('page_view', {
      pageName,
      ...additionalData
    });
  };
  
  /**
   * 跟踪用户交互
   */
  const trackInteraction = (element: string, action: string, additionalData?: Record<string, any>) => {
    trackBehavior('interaction', {
      element,
      action,
      ...additionalData
    });
  };
  
  /**
   * 跟踪转换事件
   */
  const trackConversion = (conversionType: string, value?: number, additionalData?: Record<string, any>) => {
    trackBehavior('conversion', {
      conversionType,
      value,
      ...additionalData
    });
  };
  
  /**
   * 跟踪错误事件
   */
  const trackError = (errorType: string, errorMessage: string, additionalData?: Record<string, any>) => {
    trackBehavior('error', {
      errorType,
      errorMessage,
      ...additionalData
    });
  };
  
  /**
   * 更新会话统计
   */
  const updateSessionStats = (eventType: string) => {
    switch (eventType) {
      case 'page_view':
        sessionStats.value.pageViews++;
        break;
      case 'interaction':
        sessionStats.value.interactions++;
        break;
      case 'conversion':
        sessionStats.value.conversions++;
        break;
    }
  };
  
  /**
   * 开始新会话
   */
  const startNewSession = () => {
    sessionId.value = generateSessionId();
    behaviorQueue.value = [];
    sessionStats.value = {
      pageViews: 0,
      interactions: 0,
      timeSpent: 0,
      conversions: 0
    };
    error.value = null;
  };
  
  /**
   * 启用/禁用跟踪
   */
  const setTrackingEnabled = (enabled: boolean) => {
    isTrackingEnabled.value = enabled;
    
    if (!enabled) {
      // 禁用跟踪时清空队列
      behaviorQueue.value = [];
    }
  };
  
  /**
   * 获取会话摘要
   */
  const getSessionSummary = () => {
    return {
      sessionId: sessionId.value,
      duration: sessionDuration.value,
      stats: sessionStats.value,
      queueSize: queueSize.value,
      isTrackingEnabled: isTrackingEnabled.value
    };
  };
  
  /**
   * 清除错误状态
   */
  const clearError = () => {
    error.value = null;
  };

  /**
   * 显示引流提示
   */
  const showLeadPrompt = async (config: any) => {
    try {
      // 这里应该实现显示引流提示的逻辑
      // 例如显示弹窗、跳转页面等
      console.log('显示引流提示:', config);

      // 记录引流行为
      await trackBehavior('lead_prompt_shown', {
        trigger: config.trigger,
        strategy: config.strategy,
        timestamp: Date.now()
      });
    } catch (err: any) {
      console.error('显示引流提示失败:', err);
      error.value = err.message || '显示引流提示失败';
    }
  };

  // ============= 生命周期处理 =============
  
  // 页面卸载时发送剩余数据
  const handleBeforeUnload = () => {
    if (behaviorQueue.value.length > 0) {
      // 使用 sendBeacon 发送数据，确保在页面卸载时也能发送
      const data = JSON.stringify(behaviorQueue.value);
      navigator.sendBeacon('/api/track-behavior', data);
    }
  };
  
  // 注册页面卸载事件
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', handleBeforeUnload);
  }

  return {
    // 状态
    sessionId,
    behaviorQueue,
    isLoading,
    error,
    isTrackingEnabled,
    sessionStats,

    // 计算属性
    queueSize,
    hasUnsentData,
    sessionDuration,

    // 方法
    trackBehavior,
    flushBehaviorQueue,
    trackPageView,
    trackInteraction,
    trackConversion,
    trackError,
    startNewSession,
    setTrackingEnabled,
    getSessionSummary,
    clearError,
    showLeadPrompt // ✅ 新增
  };
});
